import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

// Estados: null = verificando sessao | objeto = autenticado | false = nao autenticado
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // Guarda de sequencia: evita que uma resposta /me antiga (disparada no mount)
  // sobrescreva um login/logout mais recente (race condition de cold-start).
  const seqRef = useRef(0);

  const refreshMe = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const { data } = await api.get("/auth/me");
      if (seq === seqRef.current) setUser(data);
      return data;
    } catch {
      if (seq === seqRef.current) setUser(false);
      return false;
    }
  }, []);

  useEffect(() => { refreshMe(); }, [refreshMe]);

  const login = useCallback(async (identifier, password, remember = false) => {
    try {
      const { data } = await api.post("/auth/login", { identifier, password, remember });
      seqRef.current++; // invalida qualquer /me em voo
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    finally { seqRef.current++; setUser(false); }
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
